import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, FolderOpen, Search, CheckCircle, Loader2, Sparkles, AlertTriangle } from "lucide-react";

/**
 * Map an Etsy line item to the folder holding its print files.
 *
 * Opened from an ntfy notification, so it is phone-first: one card per unmapped item, the model's
 * suggestion pre-selected, its shortlist as one-tap options, and a search box for when the model
 * missed. Approving stores a permanent title -> folder mapping, so a repeat sale of the same product
 * never asks again.
 */
export function CommissionMapPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState({});      // description -> selected path
  const [query, setQuery] = useState({});        // description -> search term
  const [results, setResults] = useState({});    // description -> search hits
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/commissions/pending/${invoiceId}`);
      setData(res);
      // Pre-select the model's suggestion: the common case is confirming it, not overriding.
      const pre = {};
      (res.items || []).forEach((it) => {
        if (it.suggested && it.suggested.length) pre[it.description] = it.suggested[0];
      });
      setChoice(pre);
    } catch (e) {
      toast.error(e.message || "Could not load this invoice");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const search = async (desc) => {
    const q = (query[desc] || "").trim();
    if (!q) return;
    try {
      const res = await api.get(`/commissions/folders?q=${encodeURIComponent(q)}&limit=25`);
      setResults((r) => ({ ...r, [desc]: res }));
    } catch (e) {
      toast.error(e.message || "Folder search failed");
    }
  };

  const approve = async (desc) => {
    const path = choice[desc];
    if (!path) { toast.error("Pick a folder first"); return; }
    setBusy((b) => ({ ...b, [desc]: true }));
    try {
      const res = await api.post("/commissions/mappings/approve", {
        description: desc,
        folder_paths: [path],
      });
      const n = res.requeued_invoices || 0;
      toast.success(n ? `Mapped. Copying files for ${n} order(s).` : "Mapped and saved.");
      await load();
    } catch (e) {
      toast.error(e.message || "Could not save the mapping");
    } finally {
      setBusy((b) => ({ ...b, [desc]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const unmapped = (data.items || []).filter((i) => i.status !== "approved");
  const done = (data.items || []).filter((i) => i.status === "approved");

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-16">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/invoices/${invoiceId}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">Map print folders</h1>
          <p className="text-sm text-muted-foreground">
            {data.client_name} &middot; {data.invoice_number}
          </p>
        </div>
      </div>

      {unmapped.length === 0 && (
        <Card>
          <CardContent className="flex items-center gap-3 py-6">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span>Every item on this order is mapped.</span>
          </CardContent>
        </Card>
      )}

      {unmapped.map((item) => {
        const opts = [];
        const seen = new Set();
        (item.suggested || []).forEach((p) => { if (!seen.has(p)) { seen.add(p); opts.push({ path: p, suggested: true }); } });
        (item.shortlist || []).forEach((s) => { if (!seen.has(s.path)) { seen.add(s.path); opts.push({ path: s.path, score: s.score }); } });
        (results[item.description] || []).forEach((s) => { if (!seen.has(s.path)) { seen.add(s.path); opts.push({ path: s.path, score: s.score, found: true }); } });

        return (
          <Card key={item.description}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-start gap-2">
                <FolderOpen className="h-4 w-4 mt-1 shrink-0" />
                <span className="break-words">{item.description}</span>
              </CardTitle>
              {item.reason ? (
                <CardDescription className="flex items-start gap-2 pt-1">
                  <Sparkles className="h-3 w-3 mt-1 shrink-0" />
                  <span>
                    {item.source || "model"} suggests this ({item.confidence}% sure): {item.reason}
                  </span>
                </CardDescription>
              ) : (
                <CardDescription className="flex items-start gap-2 pt-1">
                  <AlertTriangle className="h-3 w-3 mt-1 shrink-0 text-yellow-500" />
                  <span>No suggestion &mdash; search for the folder below.</span>
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {opts.map((o) => (
                  <label
                    key={o.path}
                    className={`flex items-start gap-2 rounded border p-2 text-sm cursor-pointer ${
                      choice[item.description] === o.path ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`f-${item.description}`}
                      className="mt-1"
                      checked={choice[item.description] === o.path}
                      onChange={() => setChoice((c) => ({ ...c, [item.description]: o.path }))}
                    />
                    <span className="break-all flex-1">{o.path}</span>
                    {o.suggested && <Badge variant="secondary" className="shrink-0">suggested</Badge>}
                    {o.found && <Badge variant="outline" className="shrink-0">search</Badge>}
                  </label>
                ))}
                {opts.length === 0 && (
                  <p className="text-sm text-muted-foreground">No candidates yet &mdash; search below.</p>
                )}
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Search folders on the share..."
                  value={query[item.description] || ""}
                  onChange={(e) => setQuery((q) => ({ ...q, [item.description]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(item.description); } }}
                />
                <Button variant="outline" onClick={() => search(item.description)}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>

              <Button
                className="w-full"
                disabled={!choice[item.description] || busy[item.description]}
                onClick={() => approve(item.description)}
              >
                {busy[item.description] ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <>Use this folder &amp; copy files</>
                )}
              </Button>
            </CardContent>
          </Card>
        );
      })}

      {done.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Already mapped</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {done.map((i) => (
              <div key={i.description} className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="break-words">{i.description}</div>
                  <div className="text-xs text-muted-foreground break-all">{(i.suggested || [])[0]}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
